import React, { useEffect, useMemo, useState } from 'react';
import { useOutletContext, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Star, CalendarClock, UsersRound, ArrowRight, Rocket, DollarSign, Truck,
  UtensilsCrossed, Users, TrendingUp, Sparkles, FileUp, Gauge,
} from 'lucide-react';
import { useRest, restFetch, RCard, RestLoading, money, RestReconnect, LogoBadge } from './restui';
import { DISH_IMG, dishPoolFor } from './RestMenu';
import { Header, ConnectTile, DailyIntel } from './RestMissionControl';
import { PulseSection, Reveal } from './RestPulse';
import { RestGamePlan } from './RestGamePlan';
import { TEAM_AVATARS } from '../../components/teamAvatars';
import { AgentHelp } from '../../components/AgentHelp';
import AgentAdviceRail from '../../components/AgentAdviceRail';
import { KitxensEffectCard } from '../../components/KitxensEffect';
import { isRestDemo, DEMO_GBP_STATUS } from '../../demo/restDemo';
import { toast } from 'sonner';

/* Home — level 1 of the OS. The restaurant arrives, the system has already done the work.
   "My restaurant has already been analyzed. Now I simply decide what to do." */

const WORKSPACES = [
  { to: 'mission-control', icon: Rocket, en: 'Mission Control', es: 'Mission Control', den: 'Your operation at a glance', des: 'Tu operación de un vistazo' },
  { to: 'revenue', icon: DollarSign, en: 'Revenue', es: 'Revenue', den: 'Sales, products, trends', des: 'Ventas, productos, tendencias' },
  { to: 'delivery', icon: Truck, en: 'Delivery', es: 'Delivery', den: 'Platforms & channel mix', des: 'Plataformas y mix de canales' },
  { to: 'menu', icon: UtensilsCrossed, en: 'Menu Intelligence', es: 'Menu Intelligence', den: 'What sells, what doesn\u2019t', es2: '', des: 'Qué vende y qué no' },
  { to: 'guests', icon: Users, en: 'Guests', es: 'Guests', den: 'Your customers', des: 'Tus clientes' },
  { to: 'marketing', icon: TrendingUp, en: 'Marketing', es: 'Marketing', den: 'Campaigns & reach', des: 'Campañas y alcance' },
  { to: 'ai-executive', icon: Sparkles, en: 'AI Executive', es: 'AI Executive', den: 'Your executive brief', des: 'Tu resumen ejecutivo' },
  { to: 'reports', icon: FileUp, en: 'Reports', es: 'Reports', den: 'Import & history', des: 'Importar e histórico' },
];

function GoogleTile({ isEs }) {
  const [st, setSt] = useState(null);
  useEffect(() => {
    if (isRestDemo()) { setSt(DEMO_GBP_STATUS); return; }
    restFetch('/google/status').then((r) => (r.ok ? r.json() : null)).then(setSt).catch(() => {});
  }, []);
  const snap = st?.gbp_snapshot;
  if (st?.gbp?.status === 'connected') {
    return (
      <Link to="/restaurant/reviews" data-testid="rest-connect-google"
        className="group flex items-center gap-3 rounded-2xl border border-emerald-200 bg-emerald-50/40 px-4 py-3.5 hover:border-emerald-400 transition-colors">
        <span className="w-9 h-9 rounded-xl bg-amber-50 border border-amber-200 flex items-center justify-center">
          <Star className="w-4 h-4 text-amber-500 fill-amber-400" />
        </span>
        <span className="flex-1 min-w-0">
          <span className="block text-[13px] font-semibold text-gray-900 tabular-nums">
            {snap?.rating ?? '—'} ★ <span className="text-gray-400 font-medium">· {(snap?.review_count ?? 0).toLocaleString()} {isEs ? 'reseñas' : 'reviews'}</span>
          </span>
          <span className="block text-[11px] text-emerald-600 font-semibold">{isEs ? 'Google conectado ✓' : 'Google connected ✓'}</span>
        </span>
        <span className="inline-flex items-center gap-1 text-[11.5px] font-bold text-gray-400 group-hover:text-emerald-600 transition-colors">
          {isEs ? 'Ver' : 'View'} <ArrowRight className="w-3 h-3" />
        </span>
      </Link>
    );
  }
  return <ConnectTile icon={Star} label="Google Rating" isEs={isEs} testid="rest-connect-google" />;
}

function WelcomeBrief({ isEs, me }) {
  const name = (me.user?.greeting || (me.user?.name || '').split(' ')[0]).split(' by ')[0];
  const br = me.restaurant?.branding || {};
  const isClient = me.role !== 'demo';
  const steps = [
    { icon: FileUp, to: '/restaurant/reports', t: isEs ? 'Importa tus datos' : 'Import your data', s: isEs ? 'Sube un reporte de ventas (POS, delivery, Excel) y tu OS cobra vida.' : 'Upload a sales report (POS, delivery, Excel) and your OS comes alive.' },
    { icon: Gauge, to: '/restaurant/connections', t: isEs ? 'Conecta tus plataformas' : 'Connect your platforms', s: isEs ? 'Toast, DoorDash, Google… tú pides, el equipo Kitxens conecta.' : 'Toast, DoorDash, Google… you ask, the Kitxens team connects.' },
  ];
  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55 }}
      className="mt-6 rounded-3xl text-white relative overflow-hidden" data-testid="rest-welcome-brief"
      style={{ background: `linear-gradient(135deg, var(--rest-accent) 0%, #141414 74%)` }}>
      {br.hero_url && (
        <div className="relative h-32 sm:h-40">
          <img src={br.hero_url} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: `center ${br.hero_pos ?? 32}%` }} />
          <span className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.05) 35%, rgba(10,10,10,0.6) 80%, color-mix(in srgb, var(--rest-accent) 42%, #101010) 100%)' }} aria-hidden="true" />
        </div>
      )}
      <div className="relative px-6 sm:px-7 lg:px-9 pb-6 sm:pb-7">
        <div className={`flex items-end gap-4 ${br.hero_url ? '-mt-6 sm:-mt-8' : 'pt-6'}`}>
          {br.logo_url && (
            <LogoBadge src={br.logo_url} alt={me.restaurant?.name} testid="rest-welcome-logo"
              className="w-[76px] h-[76px] sm:w-24 sm:h-24 rounded-full shadow-2xl ring-4 ring-white/25 shrink-0 relative z-[1]" />
          )}
          <div className="min-w-0 self-end pb-0.5">
            <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/70 flex items-center gap-2 flex-wrap">
              <span>Daily Brief</span>
              {isClient && (
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-400/20 border border-amber-300/40 text-amber-200 px-2 py-0.5 normal-case tracking-normal" data-testid="rest-client-badge">
                  ★ {isEs ? 'Cliente Kitxens · Acceso completo' : 'Kitxens Client · Full access'}
                </span>
              )}
            </p>
            <h2 className="mt-1 text-[19px] sm:text-[22px] lg:text-[24px] font-semibold leading-snug">
              {isEs ? <>{name}, tu Restaurant AI OS está listo. Vamos a llenarlo de vida.</> : <>{name}, your Restaurant AI OS is ready. Let's bring it to life.</>}
            </h2>
          </div>
        </div>
        <p className="mt-3 text-[13.5px] text-white/75 max-w-2xl leading-relaxed">
          {isEs
            ? 'Todo lo que viste en la demo — brief diario, Efecto Kitxens, inteligencia de menú — se activa con TUS datos. Elige cómo empezamos:'
            : 'Everything you saw in the demo — daily brief, Kitxens Effect, menu intelligence — activates with YOUR data. Choose how we start:'}
        </p>
        <div className="mt-5 grid grid-cols-1 lg:grid-cols-3 gap-3.5">
          {steps.map((c, i) => (
            <Link key={i} to={c.to} className="rounded-2xl bg-black/25 backdrop-blur-md p-4 border border-white/10 hover:border-white/35 transition-colors group" data-testid={`rest-welcome-step-${i + 1}`}>
              <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-white/60 flex items-center gap-1.5"><c.icon className="w-3.5 h-3.5" /> {isEs ? `Opción ${i + 1}` : `Option ${i + 1}`}</p>
              <p className="mt-1.5 text-[14px] font-semibold group-hover:text-white">{c.t}</p>
              <p className="mt-1 text-[12px] text-white/60 leading-relaxed">{c.s}</p>
            </Link>
          ))}
          <a href={`https://wa.me/14085901102?text=${encodeURIComponent(isEs ? `Hola Kitxens, soy ${me.restaurant?.name || ''}. Quiero que suban y formateen mis datos por mí.` : `Hi Kitxens, this is ${me.restaurant?.name || ''}. I'd like you to upload and format my data for me.`)}`}
            target="_blank" rel="noopener noreferrer" className="rounded-2xl bg-black/25 backdrop-blur-md p-4 border border-white/10 hover:border-white/35 transition-colors group" data-testid="rest-welcome-step-3">
            <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-white/60 flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" /> {isEs ? 'Servicio premium' : 'Premium service'}</p>
            <p className="mt-1.5 text-[14px] font-semibold group-hover:text-white">{isEs ? 'Kitxens lo hace por ti' : 'Kitxens does it for you'}</p>
            <p className="mt-1 text-[12px] text-white/60 leading-relaxed">{isEs ? 'Nosotros subimos y formateamos tus datos hasta que llegue tu API. Todo perfecto, sin fricción.' : 'We upload and format your data until your API arrives. Everything perfect, zero friction.'}</p>
          </a>
        </div>
        <div className="mt-6 flex flex-wrap items-center gap-3" data-testid="rest-welcome-actions">
          <Link to="/restaurant/settings" className="inline-flex items-center justify-center gap-2 rounded-full bg-white text-gray-900 font-bold text-[13px] px-5 py-2.5 hover:scale-[1.02] transition-transform shadow-lg">
            🎨 {isEs ? 'Personaliza tu marca (logo, banner, colores)' : 'Customize your brand (logo, banner, colors)'}
          </Link>
          <a href={WA} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] text-white font-bold text-[13px] px-5 py-2.5 hover:opacity-90 transition-opacity">
            {isEs ? 'Hablar con Kitxens' : 'Talk to Kitxens'}
          </a>
        </div>
      </div>
    </motion.div>
  );
}

/* Executive Brief — Rachel opens every login as a conversation: priorities, impact, confidence.
   Product Vision 2027: recommendations over reports, a conversation over another dashboard. */
function ExecutiveBrief({ isEs, me, d }) {
  const [open, setOpen] = useState(() => { try { return !sessionStorage.getItem('kx_exec_brief_seen'); } catch { return true; } });
  const rachel = TEAM_AVATARS.find((a) => a.key === 'rachel') || {};
  if (!open || !d?.has_data) return null;
  const name = (me.user?.greeting || (me.user?.name || '').split(' ')[0]).split(' by ')[0];
  const delta = d.prev_week?.total > 0 ? (100 * (d.week.total - d.prev_week.total) / d.prev_week.total) : null;
  const impact = d.week?.total ? d.week.total * 0.04 : null;
  const topMix = (d.delivery_mix || [])[0];
  const mixLabel = topMix && (topMix.key === 'pos' ? (isEs ? 'tu sala (POS)' : 'your dining room (POS)') : topMix.key);
  const today = new Date().toLocaleDateString(isEs ? 'es-ES' : 'en-US', { weekday: 'long', day: 'numeric', month: 'long' });
  const h = new Date().getHours();
  const hi = isEs ? (h < 12 ? 'Buenos días' : h < 20 ? 'Buenas tardes' : 'Buenas noches') : (h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening');
  const dismiss = () => { try { sessionStorage.setItem('kx_exec_brief_seen', '1'); } catch { /* noop */ } setOpen(false); };
  const bubbles = [
    isEs
      ? <>{hi}{name ? <>, <b>{name}</b></> : ''}. Es {today} y ya revisé tus números — esto es lo que importa hoy.</>
      : <>{hi}{name ? <>, <b>{name}</b></> : ''}. It's {today} and I already went through your numbers — here's what matters today.</>,
    isEs
      ? <><b>Prioridad del día:</b> {delta != null
        ? (delta >= 0 ? <>vas <b>+{delta.toFixed(1)}%</b> vs la semana pasada — protege ese ritmo en las horas pico.</>
          : <>vas <b>{delta.toFixed(1)}%</b> vs la semana pasada — hoy toca recuperar terreno en el servicio fuerte.</>)
        : <>consolidar tus primeros datos para construir tu comparativa semanal.</>}</>
      : <><b>Today's priority:</b> {delta != null
        ? (delta >= 0 ? <>you're running <b>+{delta.toFixed(1)}%</b> vs last week — protect that pace at peak hours.</>
          : <>you're <b>{delta.toFixed(1)}%</b> vs last week — today is about winning ground back at your busiest service.</>)
        : <>consolidating your first data to build your weekly comparison.</>}</>,
    d.top_item ? (isEs
      ? <><b>Oportunidad:</b> si empujas <b>{d.top_item.name}</b> en horas pico, estimo <b>~{money(impact)}</b> extra esta semana. Impacto estimado, no promesa.</>
      : <><b>Opportunity:</b> if you push <b>{d.top_item.name}</b> at peak hours, I estimate <b>~{money(impact)}</b> extra this week. Estimated impact, not a promise.</>) : null,
    topMix ? (isEs
      ? <><b>A vigilar:</b> tu mayor fuente de ingresos es <b className="capitalize">{mixLabel}</b>{topMix.key !== 'pos' ? ' — ojo con las comisiones del canal.' : ' — mantén la experiencia en sala impecable.'} Confianza: <b>{delta != null ? 'alta' : 'media'}</b>, basada en tus últimos 14 días.</>
      : <><b>Watch out:</b> your biggest revenue source is <b className="capitalize">{mixLabel}</b>{topMix.key !== 'pos' ? " — keep an eye on the channel's commissions." : ' — keep the in-room experience flawless.'} Confidence: <b>{delta != null ? 'high' : 'medium'}</b>, based on your last 14 days.</>) : null,
  ].filter(Boolean);
  return (
    <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
      className="mt-6 rounded-3xl relative overflow-hidden border border-neutral-800"
      style={{ background: 'linear-gradient(150deg, #141416 0%, #1c1d21 100%)' }} data-testid="rest-exec-brief">
      <div className="px-5 sm:px-7 py-5 sm:py-6">
        <div className="flex items-center gap-3">
          {rachel.img && <img src={rachel.img} alt="Rachel" className="w-11 h-11 rounded-full object-cover ring-2 ring-[#F77F11]/60 shrink-0" data-testid="rest-exec-brief-avatar" />}
          <div className="min-w-0">
            <p className="text-[11px] font-bold text-[#F77F11] flex items-center gap-1.5">Rachel · {isEs ? 'Tu brief ejecutivo' : 'Your executive brief'} <AgentHelp agent="rachel" isEs={isEs} dark /></p>
            <p className="text-[12px] text-white/50">{isEs ? 'Lo preparé mientras no estabas. Tú decides — yo solo recomiendo.' : 'I prepared this while you were away. You decide — I only recommend.'}</p>
          </div>
          <button onClick={dismiss} aria-label={isEs ? 'Cerrar brief' : 'Dismiss brief'} data-testid="rest-exec-brief-close"
            className="ml-auto shrink-0 rounded-full text-white/40 hover:text-white text-[18px] leading-none w-8 h-8 flex items-center justify-center hover:bg-white/10 transition-colors">×</button>
        </div>
        <div className="mt-4 space-y-2.5 max-w-2xl">
          {bubbles.map((b, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35 + i * 0.55, duration: 0.45 }}
              className="rounded-2xl rounded-tl-md bg-white/[0.07] border border-white/10 px-4 py-3 text-[13.5px] leading-relaxed text-white/90"
              style={{ fontFamily: 'Georgia, serif' }} data-testid={`rest-exec-brief-line-${i + 1}`}>
              {b}
            </motion.div>
          ))}
        </div>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 + bubbles.length * 0.55 }}
          className="mt-4 flex flex-wrap items-center gap-2.5" data-testid="rest-exec-brief-actions">
          <Link to="/restaurant/ai-executive" data-testid="rest-exec-brief-plan"
            className="inline-flex items-center gap-1.5 rounded-full bg-[#F77F11] text-white font-bold text-[12.5px] px-5 py-2.5 hover:opacity-90 transition-opacity">
            {isEs ? 'Ver el plan completo' : 'See the full plan'} <ArrowRight className="w-3.5 h-3.5" />
          </Link>
          <button onClick={dismiss} data-testid="rest-exec-brief-ok"
            className="rounded-full border border-white/20 text-white/70 hover:text-white hover:border-white/50 font-bold text-[12.5px] px-5 py-2.5 transition-colors">
            {isEs ? 'Entendido, vamos al día' : "Got it, let's go"}
          </button>
        </motion.div>
      </div>
    </motion.div>
  );
}

function DailyBrief({ isEs, me, d }) {
  const br0 = me.restaurant?.branding || {};
  const [adjusting, setAdjusting] = useState(false);
  const [pos, setPos] = useState(br0.hero_pos ?? 32);
  const [savedPos, setSavedPos] = useState(br0.hero_pos ?? 32);
  const [savingPos, setSavingPos] = useState(false);
  const dragRef = React.useRef(null);

  const confirmPos = async () => {
    setSavingPos(true);
    try {
      const b = me.restaurant?.branding || {};
      const r = await restFetch('/profile', {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          logo_url: b.logo_url || '', primary_color: b.primary_color || '', phone: b.phone || '',
          website: b.social?.website || '', instagram: b.social?.instagram || '', facebook: b.social?.facebook || '',
          tiktok: b.social?.tiktok || '', hero_url: b.hero_url || '', hero_pos: Math.round(pos),
        }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setSavedPos(pos); setAdjusting(false);
      toast.success(isEs ? 'Portada actualizada ✓' : 'Cover updated ✓');
    } catch (e) { toast.error(String(e.message)); } finally { setSavingPos(false); }
  };
  const onDrag = (e) => {
    if (!adjusting || !dragRef.current) return;
    const rect = dragRef.current.getBoundingClientRect();
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    setPos(Math.max(0, Math.min(100, Math.round(100 * y / rect.height))));
  };

  if (!d?.has_data) return <WelcomeBrief isEs={isEs} me={me} />;
  const name = (me.user?.greeting || (me.user?.name || '').split(' ')[0]).split(' by ')[0];
  const br = me.restaurant?.branding || {};
  const delta = d.prev_week?.total > 0 ? (100 * (d.week.total - d.prev_week.total) / d.prev_week.total) : null;
  const lastDay = new Date(`${d.latest_day?.date}T12:00:00`).toLocaleDateString(isEs ? 'es-ES' : 'en-US', { weekday: 'long', month: 'short', day: 'numeric' });
  const today = new Date().toLocaleDateString(isEs ? 'es-ES' : 'en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  const topMix = (d.delivery_mix || [])[0];
  const mixLabel = topMix && (topMix.key === 'pos' ? (isEs ? 'el local (POS)' : 'in-store (POS)') : topMix.key);
  // dish of the day — rotates daily over this cuisine's dishes (never another kitchen's menu)
  const dishNames = dishPoolFor(me?.restaurant?.cuisine).filter((n) => DISH_IMG[n]);
  const dayIdx = Math.floor(Date.now() / 86400000) % dishNames.length;
  const dishName = d.top_item && dishNames.includes(d.top_item.name) && (dayIdx % 3 === 0) ? d.top_item.name : dishNames[dayIdx];
  const dishImg = DISH_IMG[dishName];
  const cols = [
    {
      q: isEs ? '¿Qué pasó?' : 'What happened?',
      a: isEs
        ? <>Tu último día registrado ({lastDay}) generó <b>{money(d.latest_day?.total)}</b> con <b>{d.latest_day?.orders}</b> órdenes.</>
        : <>Your latest recorded day ({lastDay}) generated <b>{money(d.latest_day?.total)}</b> from <b>{d.latest_day?.orders}</b> orders.</>,
    },
    {
      q: isEs ? '¿Por qué importa?' : 'Why does it matter?',
      a: isEs
        ? <>{delta != null ? <>Esta semana vas <b>{delta >= 0 ? `+${delta.toFixed(1)}%` : `${delta.toFixed(1)}%`}</b> vs la anterior.</> : <>Aún construyo tu comparativa semanal.</>}{topMix && <> Tu mayor fuente de ingresos es <b className="capitalize">{mixLabel}</b>.</>}</>
        : <>{delta != null ? <>This week you're running <b>{delta >= 0 ? `+${delta.toFixed(1)}%` : `${delta.toFixed(1)}%`}</b> vs last week.</> : <>Still building your weekly comparison.</>}{topMix && <> Your biggest revenue source is <b className="capitalize">{mixLabel}</b>.</>}</>,
    },
    {
      q: isEs ? '¿Qué hago hoy?' : 'What should I do next?',
      a: d.top_item
        ? (isEs
          ? <>Empuja <b>{d.top_item.name}</b>, tu producto más rentable, y asegúrate de que nunca falte en horas pico.</>
          : <>Push <b>{d.top_item.name}</b>, your top performer, and make sure it never runs out during peak hours.</>)
        : (isEs ? <>Revisa tu brief ejecutivo para el plan del día.</> : <>Check your executive brief for today's plan.</>),
    },
  ];
  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55 }}
      className="mt-6 rounded-3xl text-white relative overflow-hidden" data-testid="rest-daily-brief"
      style={{ background: `linear-gradient(135deg, var(--rest-accent) 0%, #141414 74%)` }}>
      {br.hero_url && (
        <div className={`relative h-36 sm:h-44 lg:h-52 ${adjusting ? 'cursor-ns-resize select-none' : ''}`} data-testid="rest-hero-cover" ref={dragRef}
          onPointerDown={adjusting ? (e) => { e.currentTarget.setPointerCapture?.(e.pointerId); onDrag(e); } : undefined}
          onPointerMove={adjusting ? (e) => e.buttons === 1 && onDrag(e) : undefined}>
          <img src={br.hero_url} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: `center ${adjusting ? pos : savedPos}%` }} data-testid="rest-hero-exterior" draggable={false} />
          <span className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.02) 35%, rgba(10,10,10,0.55) 75%, color-mix(in srgb, var(--rest-accent) 42%, #101010) 100%)' }} aria-hidden="true" />
          {!adjusting && (
            <button onClick={() => { setPos(savedPos); setAdjusting(true); }} data-testid="rest-hero-adjust-btn"
              className="absolute top-3 right-3 z-[2] inline-flex items-center gap-1.5 rounded-full bg-black/45 backdrop-blur-md border border-white/25 text-white text-[11.5px] font-bold px-3.5 py-1.5 hover:bg-black/65 transition-colors">
              📷 {isEs ? 'Ajustar portada' : 'Adjust cover'}
            </button>
          )}
          {adjusting && (
            <div className="absolute inset-x-0 top-0 z-[3] flex flex-wrap items-center gap-2.5 bg-black/60 backdrop-blur-md px-4 py-2.5" data-testid="rest-hero-adjust-bar">
              <span className="text-[11.5px] font-bold text-white">{isEs ? 'Arrastra la imagen o usa la barra' : 'Drag the image or use the slider'}</span>
              <input type="range" min={0} max={100} value={pos} onChange={(e) => setPos(Number(e.target.value))}
                className="flex-1 min-w-[120px] max-w-[240px] accent-[var(--rest-accent)]" data-testid="rest-hero-adjust-slider" />
              <span className="text-[11px] text-white/70 tabular-nums">{pos}%</span>
              <span className="text-[11.5px] font-semibold text-white/90">{isEs ? '¿Confirmar el cambio?' : 'Confirm the change?'}</span>
              <button onClick={confirmPos} disabled={savingPos} data-testid="rest-hero-adjust-save"
                className="rounded-full bg-[var(--rest-accent)] text-white text-[11.5px] font-bold px-4 py-1.5 hover:opacity-90 disabled:opacity-50 transition-opacity">
                {savingPos ? '…' : (isEs ? 'Guardar ✓' : 'Save ✓')}
              </button>
              <button onClick={() => { setAdjusting(false); setPos(savedPos); }} data-testid="rest-hero-adjust-cancel"
                className="rounded-full border border-white/35 text-white/85 text-[11.5px] font-bold px-3.5 py-1.5 hover:text-white transition-colors">
                {isEs ? 'Cancelar' : 'Cancel'}
              </button>
            </div>
          )}
          {dishImg && !adjusting && (
            <Link to="/restaurant/menu" data-testid="rest-hero-dish"
              className="hidden lg:flex absolute right-5 bottom-4 w-[300px] rounded-2xl overflow-hidden bg-black/45 border border-white/20 backdrop-blur-md hover:scale-[1.02] transition-transform shadow-2xl items-stretch">
              <img src={dishImg} alt={dishName} className="w-[104px] h-[86px] object-cover shrink-0" />
              <div className="p-2.5 min-w-0">
                <p className="text-[9px] uppercase tracking-[0.18em] font-bold text-white/60">{isEs ? 'Sugerencia del día · AI' : "Today's pick · AI"}</p>
                <p className="mt-0.5 text-[13px] font-bold leading-tight truncate">{dishName}</p>
                <p className="mt-0.5 text-[10.5px] text-white/60 leading-snug">{isEs ? 'Recomiéndalo en cada mesa hoy' : 'Suggest it at every table today'}</p>
              </div>
            </Link>
          )}
        </div>
      )}
      <div className="relative px-6 sm:px-7 lg:px-9 pb-6 sm:pb-7">
        <div className={`flex items-end gap-4 ${br.hero_url ? '-mt-6 sm:-mt-8' : 'pt-6'}`}>
          {br.logo_url && (
            <LogoBadge src={br.logo_url} alt={me.restaurant?.name} testid="rest-hero-logo"
              className="w-[76px] h-[76px] sm:w-24 sm:h-24 rounded-full shadow-2xl ring-4 ring-white/25 shrink-0 relative z-[1]" />
          )}
          <div className="min-w-0 self-end pb-0.5">
            <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/70 flex items-center gap-2 flex-wrap" data-testid="rest-brief-date">
              <span>Daily Brief · {today}</span>
              {me.role !== 'demo' && (
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-400/20 border border-amber-300/40 text-amber-200 px-2 py-0.5 normal-case tracking-normal" data-testid="rest-client-badge">
                  ★ {isEs ? 'Cliente Kitxens' : 'Kitxens Client'}
                </span>
              )}
            </p>
            <h2 className="mt-1 text-[19px] sm:text-[22px] lg:text-[24px] font-semibold leading-snug">
              {isEs ? <>{name}, esto es lo que importa hoy.</> : <>{name}, here's what matters today.</>}
            </h2>
            {me.user?.greeting?.includes(' by ') && (
              <p className="mt-0.5 text-[12px] font-semibold text-white/70 tracking-wide" data-testid="rest-brief-byline">
                by {me.user.greeting.split(' by ')[1]}
              </p>
            )}
          </div>
        </div>
        {dishImg && (
          <Link to="/restaurant/menu" data-testid="rest-hero-dish-mobile"
            className="lg:hidden mt-4 flex items-stretch rounded-2xl overflow-hidden bg-black/30 border border-white/15 backdrop-blur-md">
            <img src={dishImg} alt={dishName} className="w-[92px] h-[76px] object-cover shrink-0" />
            <div className="p-2.5 min-w-0">
              <p className="text-[9px] uppercase tracking-[0.18em] font-bold text-white/60">{isEs ? 'Sugerencia del día · AI' : "Today's pick · AI"}</p>
              <p className="mt-0.5 text-[13px] font-bold leading-tight truncate">{dishName}</p>
              <p className="mt-0.5 text-[10.5px] text-white/60">{isEs ? 'Recomiéndalo en cada mesa hoy' : 'Suggest it at every table today'}</p>
            </div>
          </Link>
        )}
        <div className="mt-5 grid grid-cols-1 lg:grid-cols-3 gap-3.5">
          {cols.map((c, i) => (
            <div key={i} className="rounded-2xl bg-black/25 backdrop-blur-md p-4 border border-white/10" data-testid={`rest-brief-q${i + 1}`}>
              <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-white/60">{c.q}</p>
              <p className="mt-1.5 text-[14px] leading-relaxed">{c.a}</p>
            </div>
          ))}
        </div>
        <div className="mt-6 grid grid-cols-2 gap-3 sm:flex sm:flex-wrap sm:items-center" data-testid="rest-brief-actions">
          <Link to="/restaurant/ai-executive" data-testid="rest-daily-brief-why"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-white text-gray-900 font-bold text-[13px] sm:text-[13.5px] px-4 sm:px-6 py-2.5 hover:scale-[1.02] transition-transform shadow-lg whitespace-nowrap">
            ✨ {isEs ? 'Muéstrame por qué' : 'Show me why'}
          </Link>
          <a href={`https://wa.me/14085901102?text=${encodeURIComponent(isEs ? 'Quiero crear una promoción con mi AI Team' : 'I want to create a promotion with my AI Team')}`}
            target="_blank" rel="noopener noreferrer" data-testid="rest-daily-brief-promo"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] text-white font-bold text-[13px] sm:text-[13.5px] px-4 sm:px-5 py-2.5 hover:opacity-90 transition-opacity whitespace-nowrap">
            {isEs ? 'Crear promoción' : 'Create promotion'}
          </a>
          <span className="col-span-2 text-[10.5px] uppercase tracking-wider text-white/50 font-bold">{isEs ? 'Fuente: tus datos importados' : 'Source: your imported data'}</span>
        </div>
      </div>
    </motion.div>
  );
}

/* Venue render gallery — "this is how it will look" for new openings (seed-owned branding.gallery). */
function VenueGallery({ isEs, br, name }) {
  const g = br?.gallery || [];
  if (!g.length) return null;
  return (
    <div className="mt-5" data-testid="rest-venue-gallery">
      <p className="text-[10.5px] uppercase tracking-[0.15em] font-bold text-gray-400">
        {isEs ? `Así será ${name}` : `This is how ${name} will look`}
      </p>
      <div className="mt-2 grid grid-cols-2 lg:grid-cols-4 gap-3">
        {g.slice(0, 4).map((im, i) => (
          <figure key={im.src} className="rounded-2xl overflow-hidden border border-gray-200 bg-white" data-testid={`rest-venue-photo-${i + 1}`}>
            <div className="overflow-hidden">
              <img src={im.src} alt={isEs ? im.es : im.en} loading="lazy"
                className="w-full h-32 sm:h-36 object-cover hover:scale-[1.04] transition-transform duration-500" />
            </div>
            <figcaption className="px-3 py-2 text-[11px] font-semibold text-gray-600 leading-snug">{isEs ? im.es : im.en}</figcaption>
          </figure>
        ))}
      </div>
      <p className="mt-1.5 text-[10.5px] text-gray-400">{isEs ? 'Renders del proyecto de interiorismo (Plasma, 2026).' : 'Interior design project renders (Plasma, 2026).'}</p>
    </div>
  );
}

/* Multi-location pulse — who leads, who sold most / dropped yesterday. Hidden for single-location tenants. */
function LocationsPulse({ isEs }) {
  const { data: d } = useRest('/locations?days=7');
  if (!d?.has_locations) return null;
  const best = d.locations[0];
  const yd = d.locations.map((l) => {
    const n = l.daily?.length || 0;
    if (n < 3) return null;
    const y = l.daily[n - 2].total;
    const p = l.daily[n - 3].total;
    return { name: l.name, y, pct: p ? Math.round(1000 * (y - p) / p) / 10 : null };
  }).filter(Boolean);
  const topY = [...yd].sort((a, b) => b.y - a.y)[0];
  const dropY = [...yd].filter((x) => x.pct !== null && x.pct < 0).sort((a, b) => a.pct - b.pct)[0];
  const climber = [...d.locations].filter((l) => l.delta_pct !== null && l.delta_pct > 0).sort((a, b) => b.delta_pct - a.delta_pct)[0];
  return (
    <Link to="/restaurant/locations" data-testid="rest-locations-pulse"
      className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-1.5 rounded-2xl border border-gray-200 bg-white px-4 py-3 shadow-sm hover:border-[var(--rest-accent)]/50 transition-colors">
      <span className="text-[10.5px] uppercase tracking-[0.15em] font-bold text-[var(--rest-accent)]">
        {isEs ? `Pulso de ${d.locations.length} locales` : `${d.locations.length}-location pulse`}
      </span>
      <span className="text-[12px] text-gray-600" data-testid="rest-pulse-leader">
        {isEs ? 'Líder (7 días, total grupo)' : 'Leader (7 days, group total)'}: <b className="text-gray-900">{best.name}</b> · {best.share_pct}%
      </span>
      {topY && (
        <span className="text-[12px] text-gray-600" data-testid="rest-pulse-top-yesterday">
          {isEs ? 'Ayer vendió más' : 'Top seller yesterday'}: <b className="text-gray-900">{topY.name}</b> ({money(topY.y)})
        </span>
      )}
      {dropY && (
        <span className="text-[12px] text-red-600" data-testid="rest-pulse-drop-yesterday">
          ▼ {isEs ? 'Ayer cayó' : 'Dropped yesterday'}: <b>{dropY.name}</b> {dropY.pct}%
        </span>
      )}
      {climber && (
        <span className="text-[12px] text-emerald-600" data-testid="rest-pulse-climber">
          ▲ {isEs ? 'El que más sube' : 'Fastest climber'}: <b>{climber.name}</b> +{climber.delta_pct}%
        </span>
      )}
      <span className="ml-auto text-[11px] font-bold text-gray-400">{isEs ? 'Ver locales →' : 'See locations →'}</span>
    </Link>
  );
}

/* With Kitxens vs without — the number that sells the platform, computed from real orders. */
function ImpactCard({ isEs, hasData, locations }) {
  const [period, setPeriod] = useState('year');
  const [loc, setLoc] = useState('');
  const { data: d } = useRest(`/impact?period=${period}${loc ? `&location=${encodeURIComponent(loc)}` : ''}`);
  if (d && !d.has_data) {
    return (
      <RCard className="mt-5 p-6" testid="rest-impact-teaser">
        <div className="flex flex-wrap items-center gap-4">
          <span className="w-11 h-11 rounded-2xl bg-[var(--rest-accent-soft)] flex items-center justify-center shrink-0">
            <TrendingUp className="w-5 h-5 text-[var(--rest-accent)]" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-[11px] uppercase tracking-[0.15em] text-gray-400 font-bold">{isEs ? 'El Efecto Kitxens' : 'The Kitxens Effect'}</p>
            <p className="mt-0.5 text-[14px] font-semibold text-gray-900">{hasData ? (isEs ? 'Calibrándose con tus datos reales' : 'Calibrating with your real data') : (isEs ? 'Se activa con tus datos reales' : 'Activates with your real data')}</p>
            <p className="mt-1 text-[12.5px] text-gray-500 leading-relaxed">{hasData
              ? (isEs ? 'Tu OS ya tiene ventas importadas. En cuanto acumulemos suficiente histórico verás aquí cuánto extra genera Kitxens — como en la demo, pero con TUS números.' : 'Your OS already has imported sales. Once we accumulate enough history you will see here how much extra Kitxens generates — like the demo, but with YOUR numbers.')
              : (isEs ? 'En cuanto importes tu primer reporte verás aquí cuánto extra genera Kitxens para tu restaurante — como en la demo, pero con TUS números.' : 'As soon as you import your first report you\u2019ll see here how much extra Kitxens generates for your restaurant — like the demo, but with YOUR numbers.')}</p>
          </div>
          {hasData ? (
            <a href={`https://wa.me/14085901102?text=${encodeURIComponent(isEs ? 'Quiero activar el Efecto Kitxens en mi dashboard' : 'I want to activate the Kitxens Effect on my dashboard')}`}
              target="_blank" rel="noopener noreferrer" data-testid="rest-impact-teaser-cta"
              className="inline-flex items-center gap-1.5 rounded-full bg-[#25D366] text-white px-5 py-2 text-[12.5px] font-bold hover:opacity-90 transition-opacity shrink-0">
              {isEs ? 'Activarlo con Kitxens →' : 'Activate it with Kitxens →'}
            </a>
          ) : (
            <Link to="/restaurant/reports" data-testid="rest-impact-teaser-cta"
              className="inline-flex items-center gap-1.5 rounded-full bg-[var(--rest-accent)] text-white px-5 py-2 text-[12.5px] font-bold hover:opacity-90 transition-opacity shrink-0">
              {isEs ? 'Importar mis datos →' : 'Import my data →'}
            </Link>
          )}
        </div>
      </RCard>
    );
  }
  if (!d?.has_data) return null;
  return (
    <div className="mt-5">
      <KitxensEffectCard d={d} period={period} setPeriod={setPeriod} isEs={isEs} testid="rest-impact-card"
        locations={locations} loc={loc} setLoc={setLoc} />
    </div>
  );
}

const WA = 'https://wa.me/14085901102';

function TeamConcierge({ isEs, me, daily }) {
  const { data: conn } = useRest('/connections');
  const [i, setI] = useState(0);

  const slides = useMemo(() => {
    const s = [];
    const top = daily?.actions?.[0];
    if (top) {
      s.push({
        who: 'rachel', role: 'AI Concierge',
        msg: isEs ? <>He detectado algo: <b>{top.title_es}</b> — {top.body_es}</> : <>I've spotted something: <b>{top.title_en}</b> — {top.body_en}</>,
        ctas: top.action
          ? [{ label: `${isEs ? top.action.label_es : top.action.label_en} →`, to: top.action.to, href: top.action.href, green: !!top.action.href }]
          : [{ label: isEs ? 'Revisar →' : 'Review →', to: '/restaurant/mission-control' }],
      });
    }
    const best = (conn?.connections || []).filter((c) => !c.earned && c.status === 'not_connected').sort((a, b) => b.points - a.points)[0];
    if (best) {
      s.push({
        who: 'stan', role: 'Growth',
        msg: isEs
          ? <>{best.unlocks_es} Conecta <b>{best.name}</b> y tu Intelligence Score sube <b>+{best.points} pts</b> — Rachel lo analiza por ti.</>
          : <>{best.unlocks_en} Connect <b>{best.name}</b> and your Intelligence Score goes up <b>+{best.points} pts</b> — Rachel analyzes it for you.</>,
        ctas: [{ label: `${isEs ? 'Conectar' : 'Connect'} ${best.name} →`, to: '/restaurant/connections' }],
      });
    }
    const ev = daily?.calendar?.[0];
    if (ev) {
      s.push({
        who: 'penny', role: isEs ? 'Campañas' : 'Campaigns',
        msg: isEs
          ? <><b>{ev.name}</b> es en <b>{ev.in_days} días</b>. Las campañas que funcionan se preparan con tiempo — tu AI Team puede montarla contigo.</>
          : <><b>{ev.name}</b> is in <b>{ev.in_days} days</b>. Winning campaigns are prepped ahead — your AI Team can build it with you.</>,
        ctas: [
          { label: isEs ? 'Hablar con mi AI Team' : 'Talk to my AI Team', green: true,
            href: `${WA}?text=${encodeURIComponent(isEs ? `Quiero preparar una campaña para ${ev.name}` : `I want to prep a campaign for ${ev.name}`)}` },
          { label: isEs ? 'Leer mi AI Brief →' : 'Read my AI Brief →', to: '/restaurant/ai-executive' },
        ],
      });
    }
    const city = me.restaurant?.city;
    if (city) {
      s.push({
        who: 'sonny', role: 'Social Media',
        msg: isEs
          ? <>¿Sabes qué se cuece en <b>{city}</b>? Súbete a la conversación local — el contenido oportuno trae clientes.</>
          : <>Know what's happening in <b>{city}</b>? Ride the local conversation — timely content brings guests in.</>,
        ctas: [
          { label: isEs ? 'Ver noticias locales →' : 'See local news →', href: `https://news.google.com/search?q=${encodeURIComponent(`restaurants ${city}`)}` },
          { label: 'Instagram +5 pts', to: '/restaurant/connections' },
        ],
      });
    }
    s.push({
      who: 'eva', role: isEs ? 'Operaciones' : 'Operations',
      msg: isEs
        ? <>Tu instancia <b>{me.restaurant?.instance?.os_id || 'privada'}</b> está operativa y sincronizada. Si necesitas algo del equipo Kitxens, estamos a un mensaje.</>
        : <>Your instance <b>{me.restaurant?.instance?.os_id || 'private'}</b> is operational and in sync. Need anything from the Kitxens team? We're one message away.</>,
      ctas: [{ label: isEs ? 'Hablar con Kitxens' : 'Talk to Kitxens', href: WA, green: true }],
    });
    return s;
  }, [daily, conn, isEs, me]);

  useEffect(() => {
    if (slides.length < 2) return undefined;
    const t = setInterval(() => setI((v) => v + 1), 9000);
    return () => clearInterval(t);
  }, [slides.length]);

  if (!slides.length) return null;
  const s = slides[i % slides.length];
  const av = TEAM_AVATARS.find((a) => a.key === s.who);

  return (
    <div className="mt-5 rounded-2xl bg-[#1A1A1A] p-5" data-testid="rest-team-concierge">
      <div className="flex items-start gap-4">
        <motion.img key={`av-${s.who}`} initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.35 }}
          src={av?.img} alt={av?.name} className="w-12 h-12 rounded-full object-cover border-2 border-white/20 shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-3">
            <p className="text-[10px] uppercase tracking-[0.2em] font-bold" style={{ color: 'var(--rest-accent)' }} data-testid="rest-team-name">
              {av?.name} · {s.role}
            </p>
            {conn?.score && (
              <Link to="/restaurant/connections" data-testid="rest-intel-chip"
                className="inline-flex items-center gap-1.5 rounded-full border border-white/20 px-3 py-1 text-[11px] font-bold text-white/80 hover:text-white hover:border-white/50 transition-colors shrink-0">
                <Gauge className="w-3 h-3" style={{ color: 'var(--rest-accent)' }} />
                Intelligence {conn.score.value}/{conn.score.max}
              </Link>
            )}
          </div>
          <motion.div key={`msg-${i % slides.length}`} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
            <p className="mt-1.5 text-[14.5px] text-white leading-relaxed" data-testid="rest-team-message">{s.msg}</p>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {s.ctas.map((c, ci) => c.to ? (
                <Link key={ci} to={c.to} data-testid={`rest-team-cta-${ci}`}
                  className={`inline-flex items-center rounded-full px-4 py-1.5 text-[12px] font-bold transition-opacity hover:opacity-90 ${ci === 0 ? 'bg-white text-gray-900' : 'border border-white/25 text-white/80 hover:text-white'}`}>
                  {c.label}
                </Link>
              ) : (
                <a key={ci} href={c.href} target="_blank" rel="noopener noreferrer" data-testid={`rest-team-cta-${ci}`}
                  className={`inline-flex items-center rounded-full px-4 py-1.5 text-[12px] font-bold transition-opacity hover:opacity-90 ${c.green ? 'bg-[#25D366] text-white' : (ci === 0 ? 'bg-white text-gray-900' : 'border border-white/25 text-white/80 hover:text-white')}`}>
                  {c.label}
                </a>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
      {slides.length > 1 && (
        <div className="mt-4 flex items-center gap-1.5 pl-16" data-testid="rest-team-dots">
          {slides.map((sl, di) => (
            <button key={di} onClick={() => setI(di)} aria-label={sl.who} data-testid={`rest-team-dot-${sl.who}`}
              className={`h-1.5 rounded-full transition-all ${di === i % slides.length ? 'w-5 bg-[var(--rest-accent)]' : 'w-1.5 bg-white/25 hover:bg-white/50'}`} />
          ))}
        </div>
      )}
    </div>
  );
}

function GrowthIdeas({ isEs }) {
  const { data, reload } = useRest('/growth');
  const [busy, setBusy] = React.useState(false);
  const [gate, setGate] = React.useState(null);
  const [done, setDone] = React.useState(() => { try { return JSON.parse(localStorage.getItem('kx_ideas_done') || '[]'); } catch { return []; } });
  const touchRef = React.useRef({ x: 0, y: 0 });
  const rachel = TEAM_AVATARS.find((a) => a.key === 'rachel');
  const g = data?.growth;

  const ideaKey = (idea) => idea.title_en || idea.title_es || '';
  const toggleDone = (idea) => {
    const k = ideaKey(idea);
    setDone((prev) => {
      const next = prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k];
      localStorage.setItem('kx_ideas_done', JSON.stringify(next));
      if (!prev.includes(k)) toast.success(isEs ? '✓ Idea completada — ¡a por la siguiente!' : '✓ Idea completed — on to the next!');
      return next;
    });
  };
  const onTouchStart = (e) => { const t = e.touches[0]; touchRef.current = { x: t.clientX, y: t.clientY }; };
  const onTouchEnd = (idea) => (e) => {
    const t = e.changedTouches[0];
    const dx = t.clientX - touchRef.current.x;
    const dy = Math.abs(t.clientY - touchRef.current.y);
    if (dy < 50 && dx < -70 && !done.includes(ideaKey(idea))) toggleDone(idea);
    if (dy < 50 && dx > 70 && done.includes(ideaKey(idea))) toggleDone(idea);
  };

  const generate = async () => {
    setBusy(true); setGate(null);
    try {
      const r = await restFetch('/growth/generate', { method: 'POST' });
      const j = await r.json();
      if (!r.ok) { setGate({ message_es: 'No pude generar ideas ahora mismo. Inténtalo de nuevo.', message_en: 'I couldn\u2019t generate ideas right now. Try again.' }); return; }
      if (j.insufficient_data) { setGate(j); return; }
      reload();
    } finally { setBusy(false); }
  };

  return (
    <RCard className="mt-5 p-6" testid="rest-growth-ideas">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          {rachel && <img src={rachel.img} alt="Rachel" className="w-9 h-9 rounded-full object-cover border border-gray-200" />}
          <div>
            <p className="text-[11px] uppercase tracking-[0.15em] text-gray-400 font-bold">{isEs ? 'Ideas para vender más' : 'Ideas to sell more'}</p>
            <p className="text-[12px] text-gray-500">{isEs ? 'Rachel · basadas solo en tus datos reales' : 'Rachel · grounded only in your real data'}</p>
          </div>
        </div>
        {!g && (
          <button onClick={generate} disabled={busy} data-testid="rest-growth-generate"
            className="inline-flex items-center gap-1.5 rounded-full bg-[var(--rest-accent)] hover:opacity-90 text-white px-5 py-2 text-[12.5px] font-bold transition-opacity disabled:opacity-50">
            <Sparkles className="w-3.5 h-3.5" /> {busy ? (isEs ? 'Analizando…' : 'Analyzing…') : (isEs ? 'Pídele ideas a Rachel' : 'Ask Rachel for ideas')}
          </button>
        )}
      </div>
      {gate && (
        <p className="mt-4 text-[13px] text-gray-500 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3" data-testid="rest-growth-gate">
          {isEs ? gate.message_es : gate.message_en}{' '}
          <Link to="/restaurant/connections" className="font-semibold text-[var(--rest-accent)] hover:underline">{isEs ? 'Ver conexiones →' : 'View connections →'}</Link>
        </p>
      )}
      {g && (
        <>
          <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-3">
            {(g.ideas || []).map((idea, i) => {
              const isDone = done.includes(ideaKey(idea));
              return (
                <div key={i} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd(idea)}
                  className={`relative rounded-2xl border p-4 transition-all ${isDone ? 'border-emerald-200 bg-emerald-50/50 opacity-70' : 'border-gray-200 bg-gray-50/60'}`}
                  data-testid={`rest-growth-idea-${i}`}>
                  <button onClick={() => toggleDone(idea)} data-testid={`rest-growth-idea-done-${i}`}
                    title={isDone ? (isEs ? 'Marcar como pendiente' : 'Mark as pending') : (isEs ? 'Marcar como completada' : 'Mark as done')}
                    className={`absolute top-3 right-3 w-6 h-6 rounded-full border-2 flex items-center justify-center text-[11px] font-black transition-all ${isDone ? 'border-emerald-500 bg-emerald-500 text-white' : 'border-gray-300 text-transparent hover:border-emerald-400 hover:text-emerald-400'}`}>
                    ✓
                  </button>
                  <p className={`text-[13.5px] font-semibold pr-7 ${isDone ? 'text-emerald-800 line-through' : 'text-gray-900'}`}>{isEs ? idea.title_es : idea.title_en}</p>
                  <p className="mt-1.5 text-[12px] text-gray-500 leading-relaxed">{isEs ? idea.why_es : idea.why_en}</p>
                  <p className="mt-2 text-[12px] text-gray-700 leading-relaxed"><b>{isEs ? 'Cómo:' : 'How:'}</b> {isEs ? idea.how_es : idea.how_en}</p>
                  <p className="mt-2 text-[11.5px] font-semibold" style={{ color: isDone ? '#059669' : 'var(--rest-accent)' }}>{isDone ? (isEs ? '✓ Completada' : '✓ Completed') : (isEs ? idea.impact_es : idea.impact_en)}</p>
                </div>
              );
            })}
          </div>
          <p className="mt-2 text-[10.5px] text-gray-400 lg:hidden">{isEs ? '← Desliza una idea a la izquierda para completarla · desliza a la derecha para deshacer' : '← Swipe an idea left to complete it · swipe right to undo'}</p>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <a href={`https://wa.me/14085901102?text=${encodeURIComponent(isEs ? 'Hola Kitxens, quiero ejecutar una de las ideas de crecimiento de Rachel' : 'Hi Kitxens, I want to execute one of Rachel\u2019s growth ideas')}`}
              target="_blank" rel="noopener noreferrer" data-testid="rest-growth-whatsapp"
              className="inline-flex items-center gap-1.5 rounded-full bg-[#25D366] text-white px-4 py-2 text-[12px] font-bold hover:opacity-90 transition-opacity">
              {isEs ? '¿La ejecutamos juntos? WhatsApp' : 'Execute it together? WhatsApp'}
            </a>
            <span className="text-[10.5px] uppercase tracking-wider text-gray-400 font-bold">{isEs ? `Fuente: ${g.source} · datos hasta ${g.data_through}` : `Source: ${g.source} · data through ${g.data_through}`}</span>
          </div>
        </>
      )}
      {!g && !gate && (
        <p className="mt-3 text-[12.5px] text-gray-400">
          {isEs ? 'Rachel analizará tus ventas reales (días flojos, productos estrella, canales) y te propondrá 3 acciones concretas para esta semana.' : 'Rachel will analyze your real sales (slow days, star products, channels) and propose 3 concrete actions for this week.'}
        </p>
      )}
    </RCard>
  );
}

function Snapshot({ isEs, d }) {
  const tops = d?.top_items?.length ? d.top_items : (d?.top_item ? [d.top_item] : []);
  const [ti, setTi] = useState(0);
  useEffect(() => {
    if (tops.length < 2) return undefined;
    const t = setInterval(() => setTi((v) => (v + 1) % tops.length), 4000);
    return () => clearInterval(t);
  }, [tops.length]);
  if (!d?.has_data) return null;
  const delta = d.prev_week?.total > 0 ? (100 * (d.week.total - d.prev_week.total) / d.prev_week.total) : null;
  const top = tops[ti % (tops.length || 1)];
  return (
    <RCard className="mt-5 p-5 grid grid-cols-2 gap-x-6 gap-y-4 sm:flex sm:flex-wrap sm:items-center sm:gap-x-8 sm:gap-y-3" testid="rest-home-snapshot">
      {[[isEs ? 'Ventas semana' : 'Week sales', money(d.week?.total), delta],
        [tops.length > 1 ? (isEs ? `Top seller ${ti + 1}/${tops.length}` : `Top seller ${ti + 1}/${tops.length}`) : (isEs ? 'Más vendido' : 'Top seller'),
          <motion.span key={top?.name || '—'} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }} className="inline-flex flex-col leading-tight" data-testid="rest-snapshot-topseller">
            <span className="truncate">{top?.name || '—'}</span>
            {top?.total > 0 && (
              <span className="text-[11px] font-bold text-[var(--rest-accent)] tabular-nums" data-testid="rest-snapshot-topseller-stats">
                {money(top.total)}{top?.orders ? ` · ${Number(top.orders).toLocaleString()} ${isEs ? 'uds' : 'sold'}` : ''} <span className="text-gray-400 font-semibold">· 28d</span>
              </span>
            )}
          </motion.span>],
        [isEs ? 'Ingresos último día' : 'Latest day revenue', money(d.latest_day?.total)],
        [isEs ? 'Órdenes semana' : 'Week orders', (d.week?.orders || 0).toLocaleString()]].map(([k, v, dl]) => (
          <div key={k} className="min-w-0">
            <p className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">{k}</p>
            <p className="text-[17px] font-semibold text-gray-900 tabular-nums truncate">
              {v}{dl != null && <span className={`ml-1.5 text-[11.5px] font-bold ${dl >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>{dl >= 0 ? '▲' : '▼'}{Math.abs(dl).toFixed(1)}%</span>}
            </p>
          </div>
        ))}
      <Link to="/restaurant/mission-control" data-testid="rest-open-mission-control"
        className="col-span-2 sm:ml-auto inline-flex items-center justify-center gap-1.5 rounded-full bg-[var(--rest-accent)] hover:opacity-90 text-white px-4 py-2 text-[12.5px] font-bold transition-opacity">
        {isEs ? 'Abrir Mission Control' : 'Open Mission Control'} <ArrowRight className="w-3.5 h-3.5" />
      </Link>
    </RCard>
  );
}

function CompanyCard({ isEs }) {
  const { data } = useRest('/company');
  if (!data?.has_company) return null;
  const total = (data.locations || []).reduce((s, l) => s + (l.lifetime || 0), 0);
  return (
    <RCard className="mt-5 p-5" testid="rest-company-card">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <p className="text-[11px] uppercase tracking-[0.15em] text-gray-400 font-bold">Company · {data.company}</p>
        <span className="text-[12px] font-semibold text-gray-500">{isEs ? 'Consolidado' : 'Consolidated'}: <b className="text-gray-900 tabular-nums">{money(total)}</b></span>
      </div>
      <div className="space-y-2">
        {(data.locations || []).map((l) => (
          <div key={l.id} className={`flex flex-wrap items-center gap-x-4 gap-y-1 rounded-xl border p-3 text-[12.5px] ${l.current ? 'border-[var(--rest-accent)] bg-[var(--rest-accent-soft)]' : 'border-gray-100'}`} data-testid={`rest-company-loc-${l.slug}`}>
            <span className="font-bold text-gray-900">{l.name}</span>
            {l.current && <span className="rounded-full bg-[var(--rest-accent)] text-white px-2 py-0.5 text-[9.5px] font-bold uppercase">{isEs ? 'Estás aquí' : 'You are here'}</span>}
            <span className="text-gray-400">{l.city}</span>
            <span className="ml-auto text-gray-500">{isEs ? 'Semana' : 'Week'}: <b className="text-gray-900 tabular-nums">{money(l.week)}</b></span>
            <span className="text-gray-500">{isEs ? 'Histórico' : 'Lifetime'}: <b className="text-gray-900 tabular-nums">{money(l.lifetime)}</b></span>
          </div>
        ))}
      </div>
      <p className="mt-2.5 text-[11px] text-gray-400">{isEs ? 'Cada location es independiente — cambia entre ellas desde el Workspace Switcher del menú lateral.' : 'Each location is independent — switch between them from the sidebar Workspace Switcher.'}</p>
    </RCard>
  );
}

const SERVICE_LOGOS = {
  toast: { src: '/brands/toast.svg', label: 'Toast' },
  doordash: { src: '/brands/doordash.svg', label: 'DoorDash' },
  ubereats: { src: '/brands/ubereats.svg', label: 'Uber Eats' },
  grubhub: { src: '/brands/grubhub.svg', label: 'Grubhub' },
  google_business: { src: '/brands/google.svg', label: 'Google Business' },
};

function ServicesStatus({ isEs, me }) {
  const services = Object.entries(me.restaurant?.connected_services || {});
  if (!services.length) return null;
  return (
    <RCard className="mt-5 p-5" testid="rest-home-services">
      <p className="text-[11px] uppercase tracking-[0.15em] text-gray-400 font-bold mb-3">{isEs ? 'Estado de conexiones' : 'Connected services'}</p>
      <div className="flex flex-wrap gap-2">
        {services.map(([k, v]) => {
          const on = v === 'available' || v === 'connected';
          const brand = SERVICE_LOGOS[k];
          return (
            <span key={k} className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11.5px] ${brand ? '' : 'capitalize'} ${on ? 'border-emerald-300 text-emerald-600' : 'border-gray-200 text-gray-400'}`} data-testid={`rest-service-${k}`}>
              <span className={`relative inline-flex w-1.5 h-1.5 ${on ? '' : ''}`} aria-hidden="true">
                {on && <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-60" />}
                <span className={`relative w-1.5 h-1.5 rounded-full ${on ? 'bg-emerald-500' : 'bg-gray-300'}`} />
              </span>
              {brand && <img src={brand.src} alt={brand.label} className={`w-4 h-4 object-contain ${on ? '' : 'grayscale opacity-70'}`} />}
              {brand ? brand.label : k.replace('_', ' ')}
            </span>
          );
        })}
      </div>
    </RCard>
  );
}

function Workspaces({ isEs }) {
  return (
    <div className="mt-8" data-testid="rest-home-workspaces">
      <p className="text-[11px] uppercase tracking-[0.15em] text-gray-400 font-bold mb-3">{isEs ? 'Espacios de trabajo' : 'Workspaces'}</p>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {WORKSPACES.map((w, i) => (
          <motion.div key={w.to} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.05 * i }}>
            <Link to={`/restaurant/${w.to}`} data-testid={`rest-workspace-${w.to}`}
              className="group flex items-center gap-3 rounded-2xl border border-gray-200 bg-white px-4 py-3.5 hover:border-[var(--rest-accent)] hover:-translate-y-0.5 transition-all">
              <span className="w-9 h-9 rounded-xl bg-[var(--rest-accent-soft)] flex items-center justify-center shrink-0">
                <w.icon className="w-4 h-4 text-[var(--rest-accent)]" />
              </span>
              <span className="min-w-0">
                <span className="block text-[13px] font-semibold text-gray-900 truncate">{isEs ? w.es : w.en}</span>
                <span className="block text-[11px] text-gray-400 truncate">{isEs ? w.des : w.den}</span>
              </span>
            </Link>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

function QuickActions({ isEs }) {
  const acts = [
    { to: '/restaurant/reports', icon: FileUp, en: 'Import a report', es: 'Importar reporte' },
    { to: '/restaurant/ai-executive', icon: Sparkles, en: 'Read my AI Brief', es: 'Leer mi AI Brief' },
    { to: '/restaurant/settings', icon: Users, en: 'Brand & team', es: 'Marca y equipo' },
  ];
  return (
    <div className="mt-5 flex flex-wrap gap-2" data-testid="rest-quick-actions">
      {acts.map((a) => (
        <Link key={a.to} to={a.to} className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-4 py-2 text-[12.5px] font-semibold text-gray-700 hover:border-[var(--rest-accent)] hover:text-[var(--rest-accent)] transition-colors">
          <a.icon className="w-3.5 h-3.5" /> {isEs ? a.es : a.en}
        </Link>
      ))}
    </div>
  );
}

function DemoProfileCard({ isEs }) {
  const [hidden, setHidden] = useState(() => localStorage.getItem('kx_demo_profile_done') === '1');
  const [f, setFp] = useState({ restaurant_name: localStorage.getItem('kx_welcome_restaurant') || '', city: '', pos: '' });
  const [saved, setSaved] = useState(false);
  const POS_OPTS = ['Toast', 'Square', 'Clover', 'Lightspeed', 'Otro', 'Sin POS'];
  if (hidden) return null;
  const dismiss = () => { localStorage.setItem('kx_demo_profile_done', '1'); setHidden(true); };
  const save = async () => {
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/demo-access/profile`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(f) });
      setSaved(true);
      toast.success(isEs ? 'Gracias — así te enseñamos cosas más tuyas' : 'Thanks — now we can show you things closer to yours');
      setTimeout(dismiss, 1200);
    } catch { dismiss(); }
  };
  const inp = 'w-full rounded-xl border border-gray-200 px-4 py-2.5 text-[13.5px] outline-none focus:border-[var(--rest-accent)] transition-colors';
  return (
    <RCard className="mt-5 p-5" testid="rest-demo-profile-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[14px] font-semibold text-gray-900">{isEs ? '¿Nos cuentas un poco de tu restaurante?' : 'Tell us a bit about your restaurant?'}</p>
          <p className="mt-0.5 text-[12px] text-gray-500">{isEs ? 'Totalmente opcional — solo para hablarte de TU caso, no de uno genérico.' : 'Totally optional — just so we talk about YOUR case, not a generic one.'}</p>
        </div>
        <button onClick={dismiss} data-testid="rest-demo-profile-dismiss" className="text-[12px] text-gray-400 hover:text-gray-700 shrink-0 transition-colors">{isEs ? 'Ahora no' : 'Not now'}</button>
      </div>
      <div className="mt-3 grid sm:grid-cols-2 gap-2">
        <input className={inp} placeholder={isEs ? 'Tu restaurante' : 'Your restaurant'} value={f.restaurant_name} onChange={(e) => setFp({ ...f, restaurant_name: e.target.value })} data-testid="rest-demo-profile-restaurant" />
        <input className={inp} placeholder={isEs ? 'Ciudad' : 'City'} value={f.city} onChange={(e) => setFp({ ...f, city: e.target.value })} data-testid="rest-demo-profile-city" />
      </div>
      <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
        {POS_OPTS.map((p) => (
          <button key={p} onClick={() => setFp({ ...f, pos: f.pos === p ? '' : p })} data-testid={`rest-demo-profile-pos-${p.toLowerCase().replace(/\s+/g, '-')}`}
            className={`rounded-full border px-3 py-1.5 text-[12px] font-semibold transition-colors ${f.pos === p ? 'border-[var(--rest-accent)] bg-[var(--rest-accent-soft)] text-[var(--rest-accent)]' : 'border-gray-200 text-gray-500 hover:border-gray-400'}`}>
            {p}
          </button>
        ))}
        <button onClick={save} disabled={saved || (!f.restaurant_name.trim() && !f.city.trim() && !f.pos)} data-testid="rest-demo-profile-save"
          className="ml-auto rounded-full bg-[var(--rest-accent)] text-white text-[12.5px] font-bold px-5 py-2 hover:opacity-90 disabled:opacity-35 transition-opacity">
          {saved ? '✓' : (isEs ? 'Guardar' : 'Save')}
        </button>
      </div>
    </RCard>
  );
}

function FeedbackCard({ isEs }) {
  return (
    <RCard className="mt-5 p-6 flex flex-wrap items-center gap-4" testid="rest-feedback-card">
      <div className="min-w-0 flex-1">
        <p className="text-[14px] font-semibold text-gray-900">{isEs ? '¿Te falta algo en tu OS?' : 'Missing something in your OS?'}</p>
        <p className="mt-0.5 text-[12.5px] text-gray-500">{isEs ? 'Cuéntanos qué necesitas y por qué — construimos esto contigo.' : 'Tell us what you need and why — we build this with you.'}</p>
      </div>
      <div className="flex gap-2 shrink-0">
        <a href="https://wa.me/14085901102" target="_blank" rel="noopener noreferrer" data-testid="rest-feedback-whatsapp"
          className="inline-flex items-center gap-1.5 rounded-full bg-[#25D366] text-white px-4 py-2 text-[12.5px] font-bold hover:opacity-90 transition-opacity">
          WhatsApp
        </a>
        <a href="mailto:info@kitxens.com?subject=Restaurant%20AI%20OS%20—%20Feedback" data-testid="rest-feedback-email"
          className="inline-flex items-center gap-1.5 rounded-full border border-gray-300 px-4 py-2 text-[12.5px] font-bold text-gray-700 hover:border-gray-500 transition-colors">
          Email
        </a>
      </div>
    </RCard>
  );
}

export default function RestHome() {
  const { isEs, me } = useOutletContext();
  const { data: d, err, loading } = useRest('/overview');
  const { data: daily } = useRest('/daily');
  if (loading && !d) return <RestLoading />;
  if (err) return <RestReconnect />;

  return (
    <div data-testid="rest-home">
      <Header isEs={isEs} me={me} d={d} />
      <ExecutiveBrief isEs={isEs} me={me} d={d} />
      <DailyBrief isEs={isEs} me={me} d={d} />
      <AgentAdviceRail audience="demo" isEs={isEs} className="mt-5" />
      {me.role === 'demo' && <DemoProfileCard isEs={isEs} />}
      <LocationsPulse isEs={isEs} />
      <VenueGallery isEs={isEs} br={me?.restaurant?.branding} name={me?.restaurant?.name} />
      <ImpactCard isEs={isEs} hasData={!!d?.has_data} locations={(me?.restaurant?.locations || []).map((l) => l.name)} />
      <RestGamePlan />
      <TeamConcierge isEs={isEs} me={me} daily={daily} />
      <QuickActions isEs={isEs} />
      {d?.has_data ? (
        <Snapshot isEs={isEs} d={d} />
      ) : (
        <RCard className="mt-5 p-10 text-center" testid="rest-home-empty">
          <p className="text-[11px] uppercase tracking-[0.25em] text-[var(--rest-accent)] font-bold">{isEs ? 'Bienvenido a tu Restaurant AI OS' : 'Welcome to your Restaurant AI OS'}</p>
          <h2 className="mt-3 text-[20px] font-semibold text-gray-900 max-w-xl mx-auto">
            {isEs ? 'Este sistema operativo se vuelve más inteligente cada vez que aprende de tu restaurante.' : 'This operating system becomes smarter every time it learns from your restaurant.'}
          </h2>
          <Link to="/restaurant/reports" data-testid="rest-home-connect"
            className="mt-5 inline-block rounded-xl bg-[var(--rest-accent)] hover:opacity-90 text-white font-bold text-[14px] px-7 py-2.5 transition-opacity shadow-md">
            {isEs ? 'Conectar mi restaurante →' : 'Connect My Restaurant →'}
          </Link>
        </RCard>
      )}
      <Reveal>
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="rest-connect-row">
          <GoogleTile isEs={isEs} />
          <ConnectTile icon={CalendarClock} label={isEs ? 'Reservas de hoy' : "Today's Reservations"} isEs={isEs} testid="rest-connect-reservations" />
          <ConnectTile icon={UsersRound} label={isEs ? 'Estado del equipo' : 'Labor Status'} isEs={isEs} testid="rest-connect-labor" />
        </div>
      </Reveal>
      <Reveal delay={0.08}><DailyIntel isEs={isEs} /></Reveal>
      <Reveal><CompanyCard isEs={isEs} /></Reveal>
      <Reveal><GrowthIdeas isEs={isEs} /></Reveal>
      <PulseSection isEs={isEs} />
      <Reveal><ServicesStatus isEs={isEs} me={me} /></Reveal>
      <Reveal><FeedbackCard isEs={isEs} /></Reveal>
      <Workspaces isEs={isEs} />
    </div>
  );
}
